import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const employmentApi = {
  // Event Types endpoints
  getEventTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/event-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch event types', error.status || 500);
    }
  },

  getEventTypeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/event-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Event type not found', 404);
      }
      throw new ApiError('Failed to fetch event type', error.status || 500);
    }
  },

  createEventType: async (eventTypeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/event-types`, eventTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid event type data', 400);
      }
      throw new ApiError('Failed to create event type', error.status || 500);
    }
  },

  updateEventType: async (id, eventTypeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/event-types/${id}`, eventTypeData);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Event type not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid event type data', 400);
      }
      throw new ApiError('Failed to update event type', error.status || 500);
    }
  },

  deleteEventType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/event-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Event type not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete event type', 400);
      }
      throw new ApiError('Failed to delete event type', error.status || 500);
    }
  },

  // Employment Episodes endpoints
  getEmploymentEpisodes: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employment/episodes/${employeeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employment episodes not found', 404);
      }
      throw new ApiError('Failed to fetch employment episodes', error.status || 500);
    }
  },

  // Edit (correct) fields on an existing employment episode (ADR-019)
  // data: any of { startDate, endDate, comments, sourceReference }
  //   sourceReference (ADR-049): { source?, contractId } — merged additively into
  //   payload.sourceReference; the API populates-when-absent and returns 409 when
  //   asked to overwrite a different existing contractId.
  // ADR-168: a CLOSED episode also needs EmploymentEpisodeHistoryCorrection;
  // { dryRun: true } runs the server guards and returns { dryRun, changes,
  // warnings } without writing. A closed episode's saved result carries `warnings`.
  updateEmploymentEpisode: async (episodeId, data, { dryRun = false } = {}) => {
    try {
      const query = dryRun ? '?dryRun=true' : '';
      return await authenticatedApi.put(`${API_BASE_URL}/employment/episodes/${episodeId}${query}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employment episode not found', 404);
      }
      if (error.status === 400 || error.status === 409) {
        throw new ApiError(error.data?.error || 'Invalid employment episode update', error.status);
      }
      throw new ApiError('Failed to update employment episode', error.status || 500);
    }
  },

  // Annotate an episode this system owns (ADR-136 A5) — comments + source
  // contract link only, behind the narrower EmploymentEpisodeAnnotation right.
  // data: any of { comments, sourceReference }
  annotateEmploymentEpisode: async (episodeId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employment/episodes/${episodeId}/annotation`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employment episode not found', 404);
      }
      if (error.status === 400 || error.status === 409) {
        throw new ApiError(error.data?.error || 'Invalid employment episode annotation', error.status);
      }
      throw new ApiError('Failed to annotate employment episode', error.status || 500);
    }
  },

  // ADR-174 §5 — the Contract Number verb: set, correct or clear one
  // contract's number. contractNumber is a string, or null to clear; an
  // optional reason is appended to the contract's comments. `dryRun` runs
  // every check (Region uniqueness included) and writes nothing.
  // The error keeps the body (code, regionName, conflict) for the dialog.
  setContractNumber: async (episodeId, { contractNumber, reason } = {}, { dryRun = false } = {}) => {
    try {
      const query = dryRun ? '?dryRun=true' : '';
      return await authenticatedApi.put(
        `${API_BASE_URL}/employment/episodes/${episodeId}/contract-number${query}`,
        { contractNumber, ...(reason ? { reason } : {}) }
      );
    } catch (error) {
      if (error.status === 400 || error.status === 403 || error.status === 404 || error.status === 409) {
        throw new ApiError(error.data?.error || 'The contract number could not be saved', error.status, error.data || null);
      }
      throw new ApiError('Failed to save the contract number', error.status || 500);
    }
  },

  // Edit (correct) fields on an existing employment event (ADR-019)
  // data: any of { actorType, effectiveOn, processedAt, eventValidationState }
  // ADR-168: same { dryRun } option and closed-contract rule as the episode edit.
  updateEmploymentEvent: async (eventId, data, { dryRun = false } = {}) => {
    try {
      const query = dryRun ? '?dryRun=true' : '';
      return await authenticatedApi.put(`${API_BASE_URL}/employment/events/${eventId}${query}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employment event not found', 404);
      }
      if (error.status === 400 || error.status === 409) {
        throw new ApiError(error.data?.error || 'Invalid employment event update', error.status);
      }
      throw new ApiError('Failed to update employment event', error.status || 500);
    }
  },

  // Contract amendments (ADR-143 §9) — the episode-addressed amendment
  // surface. NOT the trigger endpoint: amendments ride their own routes
  // under the ContractAmendment permission, and the backend derives the
  // actor from the authenticated principal.
  // data: { changes: {termEndDate}, effectiveOn?, signedOn?, reason,
  //         externalReference?, endReasonText? }
  createContractAmendment: async (episodeId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employment/episodes/${episodeId}/amendments`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employment episode not found', 404);
      }
      if (error.status === 400 || error.status === 403) {
        // ADR-171 C2 — keep the body: a SECONDARY_OUTLIVES_PRIMARY refusal
        // carries the contracts the UI offers to end too
        throw new ApiError(error.data?.error || 'Invalid contract amendment', error.status, error.data || null);
      }
      throw new ApiError('Failed to create contract amendment', error.status || 500);
    }
  },

  // Cancel a PENDING contract amendment (ADR-143 §7.1). data: { reason }
  cancelContractAmendment: async (episodeId, eventId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employment/episodes/${episodeId}/amendments/${eventId}/cancel`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Contract amendment not found', 404);
      }
      if (error.status === 400 || error.status === 403) {
        throw new ApiError(error.data?.error || 'This amendment cannot be cancelled', error.status);
      }
      throw new ApiError('Failed to cancel contract amendment', error.status || 500);
    }
  },

  // Revise a PENDING contract amendment in place (ADR-176, admin-only).
  // data: { revisionReason, changes?, effectiveOn?, signedOn?, reason?,
  //         externalReference?, endReasonText?, endOtherContractsToo? }
  reviseContractAmendment: async (episodeId, eventId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employment/episodes/${episodeId}/amendments/${eventId}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Contract amendment not found', 404);
      }
      if (error.status === 400 || error.status === 403) {
        throw new ApiError(error.data?.error || 'This amendment cannot be revised', error.status, error.data || null);
      }
      throw new ApiError('Failed to revise contract amendment', error.status || 500);
    }
  },

  // Change records (ADR-143 §12) — the observation stream's read surface.
  //
  // DEPRECATED (ADR-145 §3): superseded by employmentChangeLogApi
  // .getEmploymentChangeLogReport, which returns the same rows with their
  // values already resolved to names and with the org dimension the reports
  // family carries. Two endpoints over one table is drift waiting to happen.
  // No caller remains in this repo; the server route is removed one release
  // after this one. Do not add a new caller.
  //
  // params: { field, employeeId, detector, actorType, detectedFrom,
  //           detectedTo, unexplainedTermsOnly, cursor, limit }
  getChangeRecords: async (params = {}) => {
    try {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
      ).toString();
      return await authenticatedApi.get(`${API_BASE_URL}/employment/change-records${query ? `?${query}` : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch change records', error.status || 500);
    }
  },

  // The §3 integrity summary: unexplained term movement, last N days.
  // KEPT by ADR-145 §3 — it returns grouped counts, not rows, so it is a
  // genuinely different shape and stays as the dashboard strip's source.
  getChangeRecordIntegrity: async (days = 30) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employment/change-records/integrity?days=${days}`);
    } catch (error) {
      throw new ApiError('Failed to fetch the change-record integrity summary', error.status || 500);
    }
  },

  // Employment Events endpoints
  triggerEmploymentEvents: async (eventData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/events/trigger`, eventData);
    } catch (error) {
      if (error.status === 400) {
        const errorMessage = error.data?.error || 'Invalid event data';
        const errorDetails = error.data?.details;

        // If there are validation details, include them in the error message
        // ADR-171 C2 — the body rides along (reasonCode, refusal) so a
        // refusal the UI can act on reaches it
        if (errorDetails) {
          throw new ApiError(`${errorMessage}: ${errorDetails}`, 400, error.data || null);
        }

        throw new ApiError(errorMessage, 400, error.data || null);
      }
      // ADR-171 C4 — the Region limit refusal (409 OPEN_CONTRACT_LIMIT_REACHED)
      // says why in its body; surface it rather than a generic failure
      if (error.status === 409 && error.data?.error) {
        throw new ApiError(error.data.error, 409, error.data);
      }
      // ADR-183 — a sync-owned contract changed by a non-admin: the body says
      // which system maintains it and where to make the change
      if (error.status === 403 && error.data?.code === 'SYNC_OWNED_CONTRACT') {
        throw new ApiError(error.data.details || error.data.error, 403, error.data);
      }
      throw new ApiError('Failed to trigger employment events', error.status || 500);
    }
  },

  // ADR-165 A — contract deletion (delete EmploymentEpisode). The preview
  // writes nothing; the delete refuses the primary and the only contract (409,
  // with `blockers` in the body).
  // ADR-180 — read-only view of a scheduled successor contract
  getRenewalPreview: async (episodeId, eventId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employment/episodes/${episodeId}/renewals/${eventId}/preview`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Scheduled replacement not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'This replacement is no longer scheduled', 409, error.data || null);
      }
      throw new ApiError(error.data?.error || 'Failed to load the scheduled replacement', error.status || 500);
    }
  },

  previewEpisodeDeletion: async (episodeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employment/episodes/${episodeId}/deletion-preview`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to preview the contract deletion', error.status || 500);
    }
  },

  deleteEmploymentEpisode: async (episodeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employment/episodes/${episodeId}`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to delete the contract', error.status || 500);
    }
  },

  // ADR-165 B — the overlapping position legs report (read-only) and the
  // per-person repair behind its "Repair this person" button (ADR-164).
  // params: any of { kind, action, employeeId }
  getOverlappingPositionLegsReport: async (params = {}) => {
    try {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString();
      return await authenticatedApi.get(`${API_BASE_URL}/employment/repairs/overlapping-position-legs${query ? `?${query}` : ''}`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to load the overlapping position legs report', error.status || 500);
    }
  },

  repairPositionLegsForEmployee: async (employeeId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employment/repairs/overlapping-position-legs`, {
        dryRun: false,
        employeeIds: [employeeId]
      });
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to repair the position legs', error.status || 500);
    }
  },

  // ADR-166 — Employment Episode Discrepancies report (read-only).
  // params: any of { category, severity, check, employeeId }
  getEmploymentEpisodeDiscrepancies: async (params = {}) => {
    try {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString();
      return await authenticatedApi.get(`${API_BASE_URL}/reports/employment-episode-discrepancies${query ? `?${query}` : ''}`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to load the employment episode discrepancies report', error.status || 500);
    }
  },
};
